package com.eladfinish.bible.contest.screens.openQuestions

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.navigation.NavigationCommand
import com.eladfinish.bible.contest.screens.openQuestions.model.LinkTopicItemEntry
import com.eladfinish.bible.contest.screens.openQuestions.model.QuestionItem
import com.eladfinish.bible.contest.screens.openQuestions.model.TopicItem
import com.eladfinish.bible.contest.ui.models.AlternateRowData
import dev.icerock.moko.resources.StringResource
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf

@Immutable
data class OpenQuestionsState(
    val allQuestions: ImmutableList<String>,
    val topicItemList: ImmutableList<TopicItem>,
    val predefinedTopics: ImmutableList<String>,
    val sheetNumbers: ImmutableList<Pair<Int, String>>,
    val questionItems: ImmutableList<QuestionItem> = persistentListOf(),
    val dialogState: DialogState,
    val isLoading: Boolean = false,
    val currentTopic: TopicItem? = null,
    val topicTitle: String = "",
    val questionsShownCount: Int = 0,
    /** True when the screen was opened from the Random Verse screen to show a cocktail of verses. */
    val isMultipleRandomVersesMode: Boolean = false,
    /** Non-empty while a custom-sheet sheet-picker TableOfContents is active. */
    val pendingCustomSheetUrl: String = "",
    /** Non-empty while a special link-topic book-picker is active (Numbers / Education Ministry). */
    val pendingLinkEntries: ImmutableList<LinkTopicItemEntry> = persistentListOf(),
    val pendingSpecialTopicType: SpecialTopicType? = null,
) : StateKt {
    @Stable
    sealed class DialogState {
        data class Help(
            val isDontShowAgainChecked: Boolean,
            val content: String,
        ) : DialogState()

        data class TableOfContents(
            val tableOfContentsItems: ImmutableList<AlternateRowData>,
        ) : DialogState()

        data class ChapterDialog(
            val chapterData: ChapterData,
        ) : DialogState()

        data class GeneralMessage(
            val title: String,
            val content: String,
        ) : DialogState()

        /** Form for submitting a user-contributed question to the developer's Google Form. */
        data class AddQuestion(
            val question: String = "",
            val answer: String = "",
            val firstName: String = "",
            val lastName: String = "",
            val email: String = "",
            val isSubmitting: Boolean = false,
        ) : DialogState()

        /** URL input for loading questions from the user's own Google Sheet. */
        data class CustomSheetInput(
            val url: String = "",
            val isLoading: Boolean = false,
        ) : DialogState()

        data object None : DialogState()
    }
}

enum class AddQuestionFormField { QUESTION, ANSWER, FIRST_NAME, LAST_NAME, EMAIL }

/** Topics that contain links to sub-sheets instead of direct Q&A data. */
enum class SpecialTopicType {
    /** "מספרים לפי ספרים" — numbers-in-Bible topic; answer is extracted from bold words. */
    NUMBERS_BY_BOOKS,

    /** "שאלות לפי ספרים - משרד החינוך" — loads standard Q&A from the linked sub-sheet. */
    EDUCATION_MINISTRY,
}

sealed interface OpenQuestionsAction : ActionKt {
    // Initialization
    data object Initialize : OpenQuestionsAction
    data object LoadTopics : OpenQuestionsAction

    /** Pull to refresh: fetches the questions on screen again (the topics, when there are none). */
    data object ReloadShownQuestions : OpenQuestionsAction

    // Topic selection — from dialog (index into the full list including predefined items at top)
    data class TopicFromDialogSelected(val index: Int) : OpenQuestionsAction
    data class TopicSelected(val topic: TopicItem) : OpenQuestionsAction
    data class LinkTopicSelected(
        val url: String,
        val sheetId: String,
        val topicName: String,
    ) : OpenQuestionsAction

    data class SheetNumberSelected(
        val sheetId: String,
        val topicName: String,
    ) : OpenQuestionsAction

    data object LoadPairsAsQuestions : OpenQuestionsAction

    /** User selected a book/sub-topic from the special-topic link picker. */
    data class LinkEntrySelected(
        val entry: LinkTopicItemEntry,
        val type: SpecialTopicType,
    ) : OpenQuestionsAction

    // Questions state
    data class QuestionsLoaded(
        val items: ImmutableList<QuestionItem>,
        val title: String,
    ) : OpenQuestionsAction

    data class ToggleAnswerVisibility(val index: Int) : OpenQuestionsAction
    data object LoadMoreQuestions : OpenQuestionsAction
    data object RefreshQuestions : OpenQuestionsAction

    // Long-press context menu on a question
    data class CopyQuestion(val index: Int) : OpenQuestionsAction
    data class ShowChapterForQuestion(val index: Int) : OpenQuestionsAction
    data class SearchRegexForQuestion(val index: Int) : OpenQuestionsAction

    // Add Question form
    data object AddQuestion : OpenQuestionsAction
    data class AddQuestionFieldChanged(
        val field: AddQuestionFormField,
        val value: String,
    ) : OpenQuestionsAction

    data object SubmitAddQuestion : OpenQuestionsAction

    // Create Your Own Q&A
    data object CreateYourOwnQnA : OpenQuestionsAction
    data class CustomSheetUrlChanged(val url: String) : OpenQuestionsAction
    data object SubmitCustomSheetUrl : OpenQuestionsAction
    data class CustomSheetPositionSelected(val url: String, val position: Int) : OpenQuestionsAction

    // Dialogs
    data object HelpClicked : OpenQuestionsAction
    data class ToggleHelp(val shouldShowNextTime: Boolean) : OpenQuestionsAction
    data object HideDialog : OpenQuestionsAction
    data class ShowTableOfContents(val items: ImmutableList<AlternateRowData>) : OpenQuestionsAction
    data class ShowChapterDialog(val chapterData: ChapterData) : OpenQuestionsAction
    data class ShowGeneralMessage(val title: String, val content: String) : OpenQuestionsAction
}

sealed interface OpenQuestionsEffect : EffectKt {
    data class ShowToast(val message: String) : OpenQuestionsEffect
    data class ShowToastRes(val message: StringResource) : OpenQuestionsEffect
    data object ScrollToTop : OpenQuestionsEffect
    data object ShowNoInternetError : OpenQuestionsEffect
    data class CopyToClipboard(val text: String) : OpenQuestionsEffect
    data class NavigateToSearch(val navigation: NavigationCommand.Navigate) : OpenQuestionsEffect
}
